<template>
  <div class="card-recipe-page">
    <div>
      <div
        class="card-recipe-signature"
        :class="{ 'is-signed': isSigned }"
        v-if="signature"
      >
        <signature-banner
          :signature="signature"
          :discarding="discardingSignature"
          @retry="retrySignature"
          @discard="discardSignature"
        >
          <button
            v-if="isSigned"
            slot="actions"
            type="button"
            class="btn btn-sm btn-icon btn-icon-left banner-btn-whatsapp"
            :class="{ loading: sendingWhatsapp, tooltip: !isWhatsappSendEnabled }"
            data-tooltip="Envio pelo WhatsApp não habilitado"
            :disabled="sendingWhatsapp"
            @click="sendWhatsapp"
          ><fa-icon :icon="['fab', 'whatsapp']" />Enviar pelo WhatsApp</button>
          <st-button-report
            v-if="isSigned"
            slot="actions"
            type="pdf"
            button-class="btn-sm btn-primary"
            label="Imprimir receita assinada"
            :icon="['fal', 'file-signature']"
            :options="null"
            :route="printLink()"
          ></st-button-report>
        </signature-banner>
      </div>
    </div>
    <div>
    <div class="card-recipe-signed" v-if="isSigned">
      <div class="recipe-signed-sheet">
        <div class="recipe-signed-heading">
          <div class="recipe-signed-heading-icon">
            <fa-icon :icon="['fal', 'capsules']" />
          </div>
          <div class="recipe-signed-heading-text">
            <div class="recipe-signed-title">Medicamentos prescritos</div>
            <div class="recipe-signed-subtitle">{{ signedSummary }}</div>
          </div>
          <div class="recipe-signed-date" v-if="printDate && data.date">
            <fa-icon :icon="['fal', 'calendar-alt']" />
            <span>{{ data.date | date }}</span>
          </div>
        </div>
        <div class="recipe-signed-list" v-if="signedDrugs.length > 0">
          <div
            class="recipe-signed-drug"
            v-for="(drug, i) in signedDrugs"
            :key="drug.key"
          >
            <div class="recipe-signed-index"><span>{{ i + 1 }}</span></div>
            <div class="recipe-signed-content">
              <div class="recipe-signed-name">
                <span>{{ drug.name }}</span>
                <span class="recipe-signed-pill is-special" v-if="drug.specialControl">
                  <fa-icon :icon="['fal', 'exclamation-triangle']" />
                  Controle especial
                </span>
              </div>
              <div class="recipe-signed-tags" v-if="drug.amount || drug.route">
                <span class="recipe-signed-pill" v-if="drug.amount">
                  <span class="recipe-signed-pill-key">Qtd.</span>
                  {{ drug.amount }}
                </span>
                <span class="recipe-signed-pill" v-if="drug.route">
                  <span class="recipe-signed-pill-key">Via</span>
                  {{ drug.route }}
                </span>
              </div>
              <div class="recipe-signed-dosage" v-if="drug.dosage">
                <fa-icon :icon="['fal', 'clock']" />
                <span>{{ drug.dosage }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="recipe-signed-none" v-else>
          Nenhum medicamento nesta receita
        </div>
      </div>
      <div class="recipe-signed-sheet recipe-signed-notes" v-if="data.notes">
        <div class="recipe-signed-heading-icon">
          <fa-icon :icon="['fal', 'comment-lines']" />
        </div>
        <div class="recipe-signed-notes-content">
          <div class="recipe-signed-notes-title">Observações</div>
          <div class="recipe-signed-notes-text">{{ data.notes }}</div>
        </div>
      </div>
    </div>
    <fieldset
      v-else
      class="card-recipe-fieldset"
      :class="{ 'is-locked': isSignatureLocked }"
      :disabled="isSignatureLocked"
    >
    <div class="card-recipe-header">
      <div class="columns">
        <div class="column col-2 col-md-3 col-sm-12"
             :class="{'has-error': $v.data.date.$error}">
          <dx-input-date class="text-center"
                         v-model="data.date"
                         @blur="$v.data.date.$touch()"
                         :disabled="!printDate"
                         @change="getDate"
          />
        </div>
        <div class="column col-auto hide-sm">
          <label class="form-checkbox">
            <input type="checkbox" v-model="printDate">
            <i class="form-icon"></i>Imprimir data
          </label>
        </div>
        <div class="column hide-sm">
          <drug-check-status :loading="checkingDrugs" />
        </div>
        <div class="column col-auto col-md-auto col-sm-12">
          <div class="input-group" :class="{loading: loadingModel}">
            <select class="form-select" v-model="modelId"
                    :disabled="loadingModel"
                    @change="getModel">
              <option value="">Meus modelos de receituários</option>
              <option v-for="(item, i) in models" :value="item.id" :key="i">
                {{ item.name }}
              </option>
            </select>
            <button class="btn btn-neutral input-group-btn"
                    @click="openEditModel">
              Gerenciar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="card-recipe-body">
      <drug-search v-focus @select="setDrug" />
      <div class="divider" />
      <drug-alerts
        :medical-record-id="medicalRecordId"
        :drugs="data.drugs"
        @loading="checkingDrugs = $event"
      />
      <div class="empty mb-2" v-if="data.drugs.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
        </div>
        <p class="empty-title h5">Lista de medicamentos</p>
        <p class="empty-subtitle">
          Nenhum medicamento adicionado. Utilize o campo acima para realizar a busca.
        </p>
      </div>
      <item
        :data="item" v-for="(item, i) in data.drugs"
        :key="item.key"
        :dosages="dosages"
        @remove="removeDrug(i)"
        @removeDosage="removeDosage"
        v-else
      />
      <div v-if="data.drugs.length > 0">
        <textarea id="notes" rows="5" class="form-input" v-model="data.notes"
                  placeholder="Outras informações..."/>
      </div>
    </div>
    </fieldset>
    </div>
    <div>
    <div class="card-recipe-footer" v-if="!isSigned">
      <div class="d-flex centered">
        <button class="btn btn-primary btn-icon btn-icon-left"
                :disabled="!haveDrugs || pending || isSignatureRunning"
                @click="openPrintModal">
          <fa-icon :icon="['fal', 'print']"/>
          <span v-if="pending">Salvando...</span>
          <span v-else>Gerar receita</span>
        </button>
        <button class="btn btn-icon-left btn-error ml-2"
                :disabled="pending || isSignatureLocked"
                @click="remove">
          <fa-icon :icon="['fal', 'trash']"/>
          Excluir
        </button>
      </div>
    </div>
    </div>
    <modal-edit
      v-if="editModels.show"
      :show="editModels.show"
      @close="closeEditModel"
    />
    <print-recipe-modal
      v-if="printModal.show"
      :show="printModal.show"
      :medical-record-id="medicalRecordId"
      :recipe="data"
      :patient="patient"
      :professional-name="user ? user.name : ''"
      :print-date="printDate"
      :start-signing="printModal.startSigning"
      @sign="signRecipe"
      @close="printModal.show = false"
    />
  </div>
</template>

<script>
import { date } from 'src/data/validators';
import { randomHash } from '@/helpers';
import { drugName } from '@/helpers/medicine';
import { mapState } from 'vuex';
import moment from 'moment';
import * as access from 'src/data/drug-accesses';
import * as unit from 'src/data/drug-units';
import formMixin from 'src/mixins/form';
import digitalSignatureMixin from '@/mixins/digital-signature';
import SignatureBanner from '@/components/digital-signature/SignatureBanner.vue';
import { MEDICAL_RECORD_RECIPE } from '@/data/digital-signature';
import { whatsappText } from '@/helpers/digital-prescription';
import Item from './Item.vue';
import ModalEdit from '../../../../template/components/recipe/modals/Main.vue';
import DrugAlerts from '../../assistant/DrugAlerts.vue';
import DrugCheckStatus from '../../assistant/DrugCheckStatus.vue';
import DrugSearch from './DrugSearch.vue';
import PrintRecipeModal from '../modals/PrintRecipe.vue';

export default {
  mixins: [formMixin, digitalSignatureMixin],
  components: {
    Item,
    ModalEdit,
    DrugAlerts,
    DrugCheckStatus,
    DrugSearch,
    SignatureBanner,
    PrintRecipeModal,
  },
  props: {
    medicalRecordId: {
      type: String,
    },
    patient: {
      type: Object,
      default: null,
    },
    dosages: {
      type: Array,
    },
    data: {
      key: {
        type: String,
      },
      id: {
        type: String,
      },
      date: {
        type: Date,
      },
      notes: {
        type: String,
      },
      drugs: {
        type: Array,
      },
    },
  },
  data() {
    return {
      models: [],
      modelId: '',
      printDate: true,
      loadingModel: false,
      checkingDrugs: false,
      pending: false,
      printing: false,
      savingModel: false,
      debounceTimeout: null,
      editModels: {
        show: false,
      },
      printModal: {
        show: false,
        startSigning: false,
      },
    };
  },
  created() {
    this.signature = this.data.digitalSignature || null;
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    haveDrugs() {
      return this.data.drugs.length > 0;
    },
    signedDrugs() {
      return (this.data.drugs || []).map((drug, i) => {
        const quantity = Number(drug.quantity);
        const amount = drug.unit === 'continuous'
          ? unit.getName(drug.unit)
          : [
            quantity
              ? quantity.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
              : null,
            unit.getName(drug.unit),
          ].filter(v => !!v).join(' ');

        return {
          key: drug.key || `${drug.id}-${i}`,
          name: drug.name,
          amount,
          route: access.getName(drug.access),
          dosage: drug.dosage,
          specialControl: !!drug.specialControl,
        };
      });
    },
    signedSummary() {
      const total = this.signedDrugs.length;
      const special = this.signedDrugs.filter(drug => drug.specialControl).length;
      return [
        total === 1 ? '1 medicamento nesta receita' : `${total} medicamentos nesta receita`,
        special > 0 ? `${special} de controle especial` : null,
      ].filter(v => !!v).join(' · ');
    },
  },
  validations() {
    return {
      data: {
        date: { date },
      },
    };
  },
  watch: {
    data: {
      handler() {
        if (this.isSignatureLocked) {
          return;
        }
        this.pending = true;
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
          this.debounceTimeout = null;
          if (this.isSignatureLocked) {
            this.pending = false;
            return;
          }
          this.save();
        }, 1000);
      },
      deep: true,
    },
    printDate: function printDate() {
      if (this.isSignatureLocked) {
        return;
      }
      this.data.date = this.printDate ? moment().format('YYYY-MM-DD') : '';
    },
  },
  mounted() {
    this.loadModels();
    this.printDate = !!this.data.date;
  },
  methods: {
    getDate() {
      if (!this.data.date || !moment(this.data.date).isValid()) {
        this.data.date = moment().format('YYYY-MM-DD');
      }
    },
    loadModels() {
      const params = {
        active: true,
        limit: 0,
        professionalId: this.user.id,
        manipulated: false,
      };
      this.$httpX.get('/recipe-templates', { params })
        .then(({ data }) => {
          this.models = data.items;
        })
        .catch(this.$toast.error);
    },
    getModel() {
      this.loadingModel = true;
      this.$httpX.get(`/recipe-templates/${this.modelId}`)
        .then(({ data }) => {
          if (data.notes) {
            this.data.notes = data.notes;
          }
          data.drugs.forEach((drug) => {
            this.data.drugs.push({
              key: randomHash(),
              id: drug.id,
              name: drug.name,
              quantity: drug.quantity,
              unit: drug.unit,
              access: drug.access,
              dosage: drug.dosage,
              specialControl: drug.specialControl || false,
            });
          });
        })
        .catch(this.$toast.error)
        .then(() => {
          this.modelId = '';
          this.loadingModel = false;
        });
    },
    openEditModel() {
      this.editModels.show = true;
    },
    closeEditModel() {
      this.loadModels();
      this.editModels.show = false;
    },
    save() {
      const data = this.clone(this.data);
      data.printDate = this.printDate;

      if (data.date && data.date.length < 10) {
        data.date = '';
      }

      const request = !this.data.id
        ? this.$httpX
          .post(`/medical-records/${this.medicalRecordId}/recipes`, data)
        : this.$httpX
          .put(`/medical-records/${this.medicalRecordId}/recipes/${this.data.id}`, data);

      return request
        .then(({ data: result }) => {
          this.data.id = result.id;
          return true;
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao salvar este registro. Tente novamente!');
          return false;
        })
        .finally(() => {
          this.pending = false;
        });
    },
    remove() {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este receituário?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.$emit('remove');
              close();
            },
          },
        ],
      });
    },
    removeDrug(idx) {
      this.data.drugs.splice(idx, 1);
    },
    removeDosage({ id, idx }) {
      this.$emit('removeDosage', { id, idx });
    },
    setDrug(data) {
      this.data.drugs.push({
        key: randomHash(),
        id: data.id,
        name: drugName(data),
        quantity: 1,
        unit: data.unit || 'packing',
        access: data.access || 'oral',
        dosage: '',
        specialControl: data.specialControl || false,
      });
    },
    saveModel() {
      const drugs = this.data.drugs
        .filter(item => (
          item.name && item.quantity
        ));

      if (!drugs) {
        return;
      }

      this.savingModel = true;

      const names = drugs
        .map(drug => drug.name.split(' ')[0]);

      const data = {
        id: this.modelId,
        name: names.join(' + '),
        professionals: [
          { id: this.user.id, name: this.user.name },
        ],
        active: true,
        drugs,
      };

      const request = !data.id
        ? this.$httpX.post('/recipe-templates', data)
        : this.$httpX.put(`/recipe-templates/${data.id}`, data);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data: result }) => {
          const found = this.models.find(model => model.id === result.id);
          if (!found) {
            this.models.push({
              id: result.id,
              name: result.name,
              drugs: result.drugs.length,
            });
          } else {
            found.drugs = result.drugs.length;
          }
          this.modelId = result.id;
          this.$toast.show('Salvo com sucesso!');
        })
        .catch(this.$toast.error)
        .then(() => {
          this.savingModel = false;
        });
    },
    printLink() {
      return `/medical-records/${this.medicalRecordId}/recipes/${this.data.id}/print`;
    },
    openPrintModal(startSigning = false) {
      if (this.pending || !this.data.id) {
        return;
      }
      this.printModal.startSigning = startSigning === true;
      this.printModal.show = true;
    },
    isOwnDigitalSignature(data) {
      return data.type === MEDICAL_RECORD_RECIPE
        && !!this.data.id
        && data.itemId === this.data.id;
    },
    async signRecipe({ showDate, pageSize, whatsappCellphone }) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
        if (!await this.save()) {
          return;
        }
      }

      if (!this.data.id) {
        return;
      }

      this.requestDigitalSignature(this.medicalRecordId, {
        type: MEDICAL_RECORD_RECIPE,
        itemId: this.data.id,
        showDate,
        pageSize,
        whatsappCellphone: whatsappCellphone || '',
      }, () => {
        this.printModal.show = false;
      });
    },
    retrySignature() {
      this.openPrintModal(true);
    },
    sendWhatsapp() {
      this.sendDigitalSignatureWhatsapp(this.patient ? this.patient.cellphone : '');
    },
    digitalSignatureWhatsappText(documentUrl) {
      return whatsappText({
        patientName: this.patient ? this.patient.name : '',
        professionalName: this.user ? this.user.name : '',
        documentUrl,
      });
    },
    discardSignature() {
      this.discardDigitalSignature('receita');
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';
$recipe-signed-inset: $layout-spacing-lg + .2rem - $border-width;
.card-recipe-page {
    .divider {
      border-top: $border-width * 2 solid $light-color;
      margin: 1rem 0;
    }
    background-color: lighten($gray-color, 24%);
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    .card-recipe-signature {
      padding: $layout-spacing $layout-spacing 0;
      .digital-signature-banner {
        flex-wrap: wrap;
        margin-bottom: $layout-spacing;
      }
      .banner-content {
        flex: 1 1 12rem;
      }
      .banner-actions {
        flex-wrap: wrap;
        justify-content: flex-end;
        margin-left: auto;
      }
      &.is-signed {
        padding: $layout-spacing-lg $layout-spacing-lg 0;
        .digital-signature-banner {
          margin-bottom: .6rem;
        }
      }
    }
    .card-recipe-signed {
      display: flex;
      flex-direction: column;
      gap: .6rem;
      padding: 0 $layout-spacing-lg $layout-spacing-lg;
    }
    .recipe-signed-sheet {
      background-color: $light-color;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      overflow: hidden;
    }
    .recipe-signed-heading {
      align-items: center;
      border-bottom: $border-width solid $border-color;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing;
      padding: .6rem $layout-spacing-lg .6rem $recipe-signed-inset;
    }
    .recipe-signed-heading-icon {
      align-items: center;
      background-color: lighten($primary-color, 34%);
      border-radius: 50%;
      color: $primary-color;
      display: flex;
      flex: 0 0 1.8rem;
      font-size: $font-size-lg;
      height: 1.8rem;
      justify-content: center;
    }
    .recipe-signed-heading-text {
      flex: 1 1 10rem;
      min-width: 0;
    }
    .recipe-signed-title {
      color: $gray-color-900;
      font-size: $font-size-lg;
      font-weight: 700;
      line-height: 1.25;
    }
    .recipe-signed-subtitle {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin-top: .1rem;
    }
    .recipe-signed-date {
      align-items: center;
      border: $border-width solid $border-color;
      border-radius: 1rem;
      color: $gray-color-dark;
      display: inline-flex;
      font-size: $font-size-sm;
      font-weight: 600;
      gap: .3rem;
      line-height: 1;
      margin-left: auto;
      padding: .3rem .6rem;
      white-space: nowrap;
    }
    .recipe-signed-list {
      padding: .2rem 0;
    }
    .recipe-signed-drug {
      display: flex;
      gap: $layout-spacing;
      padding: .7rem $layout-spacing-lg .7rem $recipe-signed-inset;
      position: relative;
      & + .recipe-signed-drug::before {
        border-top: $border-width solid $border-color;
        content: "";
        left: $recipe-signed-inset + 2.2rem;
        position: absolute;
        right: $layout-spacing-lg;
        top: 0;
      }
    }
    .recipe-signed-index {
      display: flex;
      flex: 0 0 1.8rem;
      justify-content: center;
      span {
        align-items: center;
        background-color: $gray-color-ultra-light;
        border: $border-width solid $border-color;
        border-radius: 50%;
        color: $gray-color-dark;
        display: flex;
        font-size: $font-size-xs;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        height: 1.2rem;
        justify-content: center;
        margin-top: -.1rem;
        width: 1.2rem;
      }
    }
    .recipe-signed-content {
      flex: 1;
      min-width: 0;
    }
    .recipe-signed-name {
      align-items: center;
      color: $gray-color-900;
      display: flex;
      flex-wrap: wrap;
      font-size: $font-size;
      font-weight: 700;
      gap: .2rem .5rem;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
    .recipe-signed-tags {
      display: flex;
      flex-wrap: wrap;
      gap: .3rem;
      margin-top: .4rem;
    }
    .recipe-signed-pill {
      align-items: center;
      background-color: $light-color;
      border: $border-width solid $border-color;
      border-radius: 1rem;
      color: $gray-color-900;
      display: inline-flex;
      font-size: $font-size-xs;
      font-weight: 600;
      gap: .25rem;
      line-height: 1;
      padding: .22rem .5rem;
      white-space: nowrap;
      &.is-special {
        border-color: #F2C94C;
        color: #9A6B00;
      }
    }
    .recipe-signed-pill-key {
      color: $gray-color-dark;
      font-weight: 400;
    }
    .recipe-signed-dosage,
    .recipe-signed-notes-text {
      background-color: $gray-color-ultra-light;
      border-radius: $border-radius;
      color: $gray-color-900;
      font-size: $font-size-sm;
      line-height: 1.6;
      overflow-wrap: anywhere;
      white-space: pre-line;
    }
    .recipe-signed-dosage {
      align-items: flex-start;
      display: flex;
      gap: .4rem;
      margin-top: .5rem;
      padding: .4rem .6rem;
      svg {
        color: $gray-color-dark;
        flex-shrink: 0;
        margin-top: .2rem;
      }
    }
    .recipe-signed-none {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      padding: $layout-spacing-lg;
      text-align: center;
    }
    .recipe-signed-notes {
      align-items: flex-start;
      display: flex;
      gap: $layout-spacing;
      padding: .6rem $layout-spacing-lg .7rem $recipe-signed-inset;
      .recipe-signed-heading-icon {
        background-color: $gray-color-ultra-light;
        color: $gray-color-dark;
      }
    }
    .recipe-signed-notes-content {
      flex: 1;
      min-width: 0;
    }
    .recipe-signed-notes-title {
      align-items: center;
      color: $gray-color-900;
      display: flex;
      font-weight: 600;
      min-height: 1.8rem;
    }
    .recipe-signed-notes-text {
      padding: .5rem .7rem;
    }
    @media (max-width: $size-sm) {
      .card-recipe-signature.is-signed {
        padding: $layout-spacing $layout-spacing 0;
      }
      .card-recipe-signed {
        padding: 0 $layout-spacing $layout-spacing;
      }
      .recipe-signed-heading-text {
        flex-basis: calc(100% - 2.2rem);
      }
      .recipe-signed-date {
        margin-left: 2.2rem;
      }
    }
    .card-recipe-fieldset {
      border: 0;
      margin: 0;
      min-width: 0;
      padding: 0;
      &.is-locked .hover-link {
        opacity: .5;
        pointer-events: none;
      }
    }
    .card-recipe-header {
      background-color: lighten($gray-color, 15%);
      padding: $layout-spacing;
    }
    .card-recipe-body {
      padding: $layout-spacing-lg $layout-spacing;
    }
    .card-recipe-footer {
      padding: $layout-spacing-lg;
      text-align: center;
      button {
        min-width: 7rem;
      }
    }
  }

</style>
